import { NgxPermissionsObject, NgxPermissionsService } from 'ngx-permissions';
import { Observable, of } from 'rxjs';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';
import { BreadcrumbService } from 'xng-breadcrumb';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  FacadeInterface,
  PERMISSION_CREATE,
  PERMISSION_UPDATE,
  SharedFacadeInterface,
} from '@ellf/app-common';
import { RouterService } from '@ellf/app-routing';
import { BaseDbDto } from '@ellf/common-dto';
import { BaseDetailGuard } from './base-detail.guard';

export class DetailGuard<T extends BaseDbDto> extends BaseDetailGuard<T> {
  constructor(
    protected sharedFacade: SharedFacadeInterface<T>,
    protected facade: FacadeInterface<T>,
    breadcrumb: BreadcrumbService,
    routerService: RouterService,
    protected permissionService?: NgxPermissionsService,
  ) {
    super(breadcrumb, routerService);
  }

  protected getCreatePermission = '';
  protected getUpdatePermission = '';

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const entityId = route.params.entityId;

    return this.hasEntity(
      entityId,
      this.routerService.isViewOnlyMode(route),
      this.routerService.isAddMode(route) ||
        this.routerService.isViewOnlyMode(route),
      route,
    );
  }

  getOptions(route: ActivatedRouteSnapshot): any {
    return undefined;
  }

  hasEntity(
    id: string,
    disableForm: boolean,
    disableBreadcrumb: boolean,
    route: ActivatedRouteSnapshot,
  ): Observable<boolean> {
    return this.sharedFacade.allEntities$.pipe(
      map((items) => {
        return items.find((i) => i.id == id);
      }),

      withLatestFrom(this.permissionService?.permissions$ || of(undefined)),
      map(([item, permissions]) => {
        const createPermissions = this.hasCreatePermission(permissions);
        const updatePermissions = this.hasUpdatePermission(permissions);
        const isNew = id === '0';
        const result = isNew || item != null;
        if (result) {
          this.facade.formInitialize(
            item,
            disableForm || (isNew ? !createPermissions : !updatePermissions),
            this.getOptions(route),
            permissions ? Object.keys(permissions) : [],
          );
          this.setBreadcrumb(item, disableBreadcrumb);
        } else {
          this.sharedFacade.loadSingle(id);
        }
        return result;
      }),
      filter((res) => !!res),
      take(1),
    );
  }

  protected hasUpdatePermission(permissions: NgxPermissionsObject | undefined) {
    if (permissions) {
      return (
        permissions[this.getUpdatePermission] ||
        permissions[PERMISSION_UPDATE + '_all']
      );
    }

    return true;
  }

  protected hasCreatePermission(permissions: NgxPermissionsObject | undefined) {
    if (permissions) {
      return (
        permissions[this.getCreatePermission] ||
        permissions[PERMISSION_CREATE + '_all']
      );
    }
    return true;
  }
}
